/**
 * Library Search — User-facing semantic search across the unified Library
 * corpus (uploaded documents + wiki articles + external synced documents).
 *
 * #139 — Filed because direct navigation to `/library/search` returned a
 * 404 and the only visible search affordance was the power-user
 * Quality → Search Tester diagnostic. This page is the production search
 * workflow: title/content match, paginated, role-agnostic, and routes
 * straight into the doc reader / article reader on click.
 *
 * Backed by GET /api/v1/knowledge/search (already member-accessible).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, FileText, BookOpen, Cloud, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/library/search')({
  component: LibrarySearchPage,
});

// ─── Wire format (mirrors GET /knowledge/search) ─────────────────────────

type SearchSource = 'all' | 'uploaded' | 'wiki';

interface SearchHit {
  id: string;
  title: string;
  source: string;
  type: string;
  status: string;
  classification: string;
  size: string;
  author: string;
  updatedAt: string;
  extra: string;
  fileName: string | null;
}

interface SearchResponse {
  data: SearchHit[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function sourceIcon(source: string) {
  switch (source) {
    case 'wiki':
      return <BookOpen className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
    case 'uploaded':
      return <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    default:
      return <Cloud className="h-4 w-4 text-sky-600 dark:text-sky-400" />;
  }
}

function sourceBadgeClass(source: string): string {
  switch (source) {
    case 'wiki':
      return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20';
    case 'uploaded':
      return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
    default:
      return 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20';
  }
}

function classificationBadgeClass(c: string): string {
  switch (c) {
    case 'restricted':
      return 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
    case 'cui':
      return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

// ─── Page ────────────────────────────────────────────────────────────────

function LibrarySearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [source, setSource] = useState<SearchSource>('all');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const searchQuery = useQuery({
    enabled: submitted.trim().length > 0,
    queryKey: ['library', 'search', submitted, source, page],
    queryFn: () => {
      const params = new URLSearchParams({
        q: submitted,
        source,
        page: String(page),
        pageSize: String(pageSize),
      });
      return apiClient.get<SearchResponse>(
        `/knowledge/search?${params.toString()}`,
      );
    },
    placeholderData: keepPreviousData,
  });

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSubmitted(trimmed);
    setPage(1);
  };

  const handleOpen = (hit: SearchHit) => {
    if (hit.source === 'wiki') {
      // #144 — wiki rows navigate to the dedicated article reader route.
      navigate({ to: '/library/article/$articleId', params: { articleId: hit.id } });
    } else {
      navigate({ to: '/library/doc/$docId', params: { docId: hit.id } });
    }
  };

  const hits = searchQuery.data?.data ?? [];
  const total = searchQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Search className="h-4 w-4" /> Search
        </h2>
        <p className="text-sm text-muted-foreground">
          Search the unified Library corpus — uploaded documents, wiki articles,
          and external synced content. Results respect your classification
          clearance and ACL grants.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px] max-w-2xl">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, content, or filename..."
            className="pl-8 h-9"
            maxLength={500}
            data-testid="library-search-input"
          />
        </div>
        <Select value={source} onValueChange={(v) => { setSource(v as SearchSource); setPage(1); }}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="uploaded">Uploaded files</SelectItem>
            <SelectItem value="wiki">Wiki articles</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="submit"
          size="sm"
          className="gap-1.5 h-9"
          disabled={!query.trim() || searchQuery.isFetching}
          data-testid="library-search-submit"
        >
          {searchQuery.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Search
        </Button>
      </form>

      {searchQuery.isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Search failed: {(searchQuery.error as Error).message}
        </div>
      )}

      {submitted && !searchQuery.isLoading && hits.length === 0 && (
        <div className="border border-dashed rounded-lg p-10 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm font-medium">No results for &ldquo;{submitted}&rdquo;</p>
          <p className="text-xs text-muted-foreground mt-1">
            Try a different search term, or change the source filter.
          </p>
        </div>
      )}

      {!submitted && (
        <div className="border border-dashed rounded-lg p-10 text-center">
          <Search className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm font-medium">Enter a query to search the Library</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Searches across uploaded documents, wiki articles, and synced
            external content visible to your account.
          </p>
        </div>
      )}

      {hits.length > 0 && (
        <>
          <div className="text-xs text-muted-foreground">
            {total} {total === 1 ? 'result' : 'results'} for{' '}
            <span className="font-mono text-foreground/80">&ldquo;{submitted}&rdquo;</span>
          </div>
          <div className="border rounded-md divide-y">
            {hits.map((hit) => (
              <button
                key={`${hit.source}:${hit.id}`}
                type="button"
                onClick={() => handleOpen(hit)}
                className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3"
                data-testid="library-search-result"
              >
                <div className="mt-0.5 shrink-0">{sourceIcon(hit.source)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{hit.title}</span>
                    <Badge variant="outline" className={`text-[9px] ${sourceBadgeClass(hit.source)}`}>
                      {hit.source === 'uploaded' ? 'File' : hit.source === 'wiki' ? 'Article' : hit.source}
                    </Badge>
                    {hit.classification && hit.classification !== 'unclassified' && (
                      <Badge variant="outline" className={`text-[9px] uppercase ${classificationBadgeClass(hit.classification)}`}>
                        {hit.classification}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {hit.type}
                    {hit.size && ` · ${hit.size}`}
                    {hit.extra && ` · ${hit.extra}`}
                    {hit.author && ` · ${hit.author}`}
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0">
                  {relativeTime(hit.updatedAt)}
                </div>
              </button>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center text-xs text-muted-foreground pt-2">
              <div>
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || searchQuery.isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || searchQuery.isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
